// Sectoarele școlii și poligoanele (GET /districts; adminul le schimbă din
// Instrumente). Lecțiile, instructorii și grupele păstrează numele sectorului
// ca text, așa că un nume nu se schimbă după creare.
//
// Aceleași reguli ca pe server (src/districts/polygon-rules.ts):
// - un elev dintr-un sector fără poligon începe pe poligon, dacă există un
//   poligon activ; un sector necunoscut (o scriere veche) nu cere poligon;
// - o lecție e „pe poligon" dacă sectorul ei are poligon (și inactiv);
// - cu mai multe poligoane elevul alege o dată (User.polygonDistrict).

/** Până răspunde serverul: sectoarele de până acum. */
export const DEFAULT_DISTRICTS = [
   { name: "Botanica", hasPolygon: true, isActive: true },
   { name: "Ciocana", hasPolygon: false, isActive: true },
   { name: "Buiucani", hasPolygon: false, isActive: true },
];

/** "Botanica", " botanica ", "Botanică" → aceeași cheie (ca în MySQL). */
export function districtKey(name) {
   return String(name ?? "")
      .trim()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase();
}

function listOf(districts) {
   return Array.isArray(districts) && districts.length
      ? districts
      : DEFAULT_DISTRICTS;
}

export function findDistrict(districts, name) {
   const key = districtKey(name);
   if (!key) return null;
   return listOf(districts).find((d) => districtKey(d?.name) === key) || null;
}

/** Numele sectoarelor active, în ordinea din admin. */
export function activeDistrictNames(districts) {
   return listOf(districts)
      .filter((d) => d?.isActive)
      .map((d) => d.name);
}

export function activePolygonNames(districts) {
   return listOf(districts)
      .filter((d) => d?.isActive && d?.hasPolygon)
      .map((d) => d.name);
}

/** Toate sectoarele cu poligon (și inactive): lecțiile de acolo se numără. */
export function polygonDistrictNames(districts) {
   return listOf(districts)
      .filter((d) => d?.hasPolygon)
      .map((d) => d.name);
}

export function isPolygonDistrict(districts, sector) {
   return Boolean(findDistrict(districts, sector)?.hasPolygon);
}

export function sectorRequiresPolygon(districts, sector) {
   const district = findDistrict(districts, sector);
   return Boolean(
      district &&
         !district.hasPolygon &&
         activePolygonNames(districts).length > 0,
   );
}

/**
 * Variantele pentru un câmp „Sector" al personalului: sectoarele active plus
 * valoarea curentă, dacă e una veche/inactivă (ca să nu dispară din câmp).
 */
export function districtOptions(districts, current) {
   const names = activeDistrictNames(districts);
   const value = String(current ?? "").trim();
   if (value && !names.some((n) => districtKey(n) === districtKey(value))) {
      return [...names, value];
   }
   return names;
}

/**
 * Sectorul grupei elevului (din /auth/me), așa cum apare în listă; null dacă
 * grupa nu are sector sau sectorul nu e printre cele active.
 */
export function groupSectorOption(groupSector, options) {
   const key = districtKey(groupSector);
   if (!key) return null;
   return (options || []).find((name) => districtKey(name) === key) || null;
}

/** Elevul alege alt sector decât al grupei → îl întrebăm înainte. */
export function needsSectorConfirm(groupSector, chosen, options) {
   const own = groupSectorOption(groupSector, options);
   return Boolean(own && districtKey(chosen) !== districtKey(own));
}
